import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { InlineEdit } from "@/components/InlineEdit";
import { LiveTail } from "@/components/LiveTail";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { Sparkline } from "@/components/Sparkline";
import { useToast } from "@/components/Toast";
import type { ProxyKey, ProxyKeyUsage, RealKey } from "@/types";

type SparkPoint = { day: string; requests: number; errors: number };
type Lang = "curl" | "python" | "node" | "anthropic" | "openai";

const GATEWAY_BASE =
  typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

const KEY_PLACEHOLDER = "vlt_live_<paste-the-value-you-copied-at-creation>";

function snippetFor(lang: Lang, firstAllowedPath: string | null): string {
  const path = firstAllowedPath ?? "/v1/messages";
  const base = `${GATEWAY_BASE}/p`;
  if (lang === "curl") {
    return [
      `curl -sS ${base}${path} \\`,
      `  -H "X-Vault-Key: ${KEY_PLACEHOLDER}" \\`,
      `  -H "Content-Type: application/json"`,
    ].join("\n");
  }
  if (lang === "python") {
    return [
      `import os, httpx`,
      ``,
      `r = httpx.post(`,
      `    "${base}${path}",`,
      `    headers={`,
      `        "X-Vault-Key": os.environ["GATEKEEPER_PROXY_KEY"],`,
      `        "Content-Type": "application/json",`,
      `    },`,
      `    json={"hello": "world"},`,
      `)`,
      `print(r.status_code, r.text)`,
    ].join("\n");
  }
  if (lang === "node") {
    return [
      `const res = await fetch("${base}${path}", {`,
      `  method: "POST",`,
      `  headers: {`,
      `    "X-Vault-Key": process.env.GATEKEEPER_PROXY_KEY!,`,
      `    "Content-Type": "application/json",`,
      `  },`,
      `  body: JSON.stringify({ hello: "world" }),`,
      `});`,
      `console.log(res.status, await res.text());`,
    ].join("\n");
  }
  if (lang === "anthropic") {
    return [
      `# pip install anthropic`,
      `from anthropic import Anthropic`,
      ``,
      `client = Anthropic(`,
      `    base_url="${base}",`,
      `    api_key="ignored-but-must-be-non-empty",`,
      `    default_headers={"X-Vault-Key": os.environ["GATEKEEPER_PROXY_KEY"]},`,
      `)`,
      ``,
      `msg = client.messages.create(`,
      `    model="claude-sonnet-4-6",`,
      `    max_tokens=64,`,
      `    messages=[{"role": "user", "content": "ping"}],`,
      `)`,
      `print(msg.content[0].text)`,
    ].join("\n");
  }
  // openai
  return [
    `# pip install openai`,
    `from openai import OpenAI`,
    ``,
    `client = OpenAI(`,
    `    base_url="${base}/v1",`,
    `    api_key="ignored-but-must-be-non-empty",`,
    `    default_headers={"X-Vault-Key": os.environ["GATEKEEPER_PROXY_KEY"]},`,
    `)`,
    ``,
    `resp = client.chat.completions.create(`,
    `    model="gpt-4o-mini",`,
    `    messages=[{"role": "user", "content": "ping"}],`,
    `)`,
    `print(resp.choices[0].message.content)`,
  ].join("\n");
}

export function ProxyKeyDetail() {
  const { id } = useParams();
  const [row, setRow] = useState<ProxyKey | null>(null);
  const [usage, setUsage] = useState<ProxyKeyUsage | null>(null);
  const [real, setReal] = useState<RealKey | null>(null);
  const [series, setSeries] = useState<SparkPoint[] | null>(null);
  const [lang, setLang] = useState<Lang>("curl");
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  async function load() {
    try {
      const pk = await api<ProxyKey>(`/api/admin/proxy-keys/${id}`);
      setRow(pk);
      setUsage(await api<ProxyKeyUsage>(`/api/admin/proxy-keys/${id}/usage`));
      const ts = await api<{ days: number; series: SparkPoint[] }>(
        `/api/admin/proxy-keys/${id}/timeseries?days=7`
      );
      setSeries(ts.series);
      try {
        setReal(await api<RealKey>(`/api/admin/real-keys/${pk.real_key_id}`));
      } catch {
        /* ok — provider glyph optional */
      }
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, [id]);

  async function revoke() {
    if (!confirm("Revoke this proxy key? Consumers will start getting 401 immediately.")) return;
    try {
      await api(`/api/admin/proxy-keys/${id}/revoke`, { method: "POST" });
      toast.success("Proxy key revoked");
      await load();
    } catch (e: any) {
      setErr(e?.message ?? "revoke failed");
      toast.error("Revoke failed");
    }
  }

  const [health, setHealth] = useState<{ status: string; detail: string } | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  async function smokeTest() {
    setHealth(null);
    setHealthLoading(true);
    try {
      const r = await api<{ status: string; detail: string }>(
        `/api/admin/proxy-keys/${id}/health-check`,
        { method: "POST" }
      );
      setHealth(r);
      if (r.status === "alive") toast.success("Proxy key reaches a working upstream");
      else toast.error(`Smoke test: ${r.status}`);
    } catch (e: any) {
      setHealth({ status: "error", detail: e?.message ?? "request failed" });
    } finally {
      setHealthLoading(false);
    }
  }

  if (!row) return <div className="p-4 text-slate-500">{err ?? "Loading…"}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/proxy-keys" className="hover:text-slate-200">
          Proxy keys
        </Link>
        <span>/</span>
        <span className="text-slate-300">{row.label}</span>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {real && <ProviderGlyph provider={real.provider} size={40} />}
          <div className="min-w-0">
            <h1 className="text-3xl display truncate">
              <InlineEdit
                value={row.label}
                onSave={async (next) => {
                  await api(`/api/admin/proxy-keys/${id}`, {
                    method: "PATCH",
                    json: { label: next },
                  });
                  await load();
                }}
                placeholder="(click to set a label)"
              />
            </h1>
            <p className="text-sm text-slate-400 mono truncate">
              {row.prefix}… · project=
              <InlineEdit
                value={row.project ?? ""}
                placeholder="(unset)"
                className="inline-block min-w-[80px]"
                onSave={async (next) => {
                  await api(`/api/admin/proxy-keys/${id}`, {
                    method: "PATCH",
                    json: { project: next || null },
                  });
                  await load();
                }}
              />
              {" · "}
              {real ? (
                <Link className="hover:text-accent-500" to={`/real-keys/${real.id}`}>
                  {real.label}
                </Link>
              ) : (
                `real_key#${row.real_key_id}`
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {!row.revoked_at && (
            <button
              className="btn-secondary"
              onClick={smokeTest}
              disabled={healthLoading}
              title="Send a tiny canned request through this proxy key to confirm it reaches a working upstream"
            >
              {healthLoading ? "Testing…" : "Test"}
            </button>
          )}
          {!row.revoked_at && (
            <button className="btn-danger" onClick={revoke}>
              Revoke
            </button>
          )}
        </div>
      </div>
      {err && <div className="card text-rose-300 text-sm">{err}</div>}
      {health && (
        <div
          className={`card border text-sm ${
            health.status === "alive"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : health.status === "unauthenticated" || health.status === "upstream_error"
              ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
              : "border-amber-500/30 bg-amber-500/10 text-amber-200"
          }`}
        >
          <div className="font-semibold capitalize">{health.status.replace("_", " ")}</div>
          <div className="text-xs opacity-80 mt-1">{health.detail}</div>
        </div>
      )}

      <div className="grid grid-cols-[auto_1fr] gap-4">
        <div className="grid grid-cols-3 gap-4 min-w-0">
          <Stat label="Total requests" value={usage?.total_requests ?? 0} />
          <Stat label="Errors (4xx/5xx)" value={usage?.error_count ?? 0} />
          <Stat label="Avg latency (ms)" value={Math.round(usage?.avg_latency_ms ?? 0)} />
        </div>
        <section className="card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-sm">Last 7 days</h2>
            <span className="text-xs text-slate-500">
              {series ? `${series.reduce((s, p) => s + p.requests, 0)} requests total` : "—"}
            </span>
          </div>
          {series === null ? (
            <div className="h-16 skeleton" />
          ) : (
            <Sparkline data={series} />
          )}
        </section>
      </div>

      <section className="card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Consumer quickstart</h2>
          <button
            className="btn-secondary text-xs"
            onClick={() => {
              navigator.clipboard.writeText(
                snippetFor(lang, row.allowed_path_prefixes?.[0] ?? null)
              );
              toast.success(`Copied ${lang} snippet`);
            }}
          >
            Copy
          </button>
        </div>

        <div className="flex flex-wrap gap-1 mb-3">
          {(
            [
              ["curl", "curl"],
              ["python", "Python (httpx)"],
              ["node", "Node (fetch)"],
              ["anthropic", "Anthropic SDK"],
              ["openai", "OpenAI SDK"],
            ] as [Lang, string][]
          ).map(([l, label]) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-3 py-1 rounded-md text-xs transition-colors ${
                lang === l
                  ? "bg-accent-500/15 text-accent-500 border border-accent-500/30"
                  : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <pre className="bg-black/60 rounded-lg p-3 mono text-xs text-slate-300 overflow-auto">
          {snippetFor(lang, row.allowed_path_prefixes?.[0] ?? null)}
        </pre>
        <p className="text-xs text-slate-500 mt-2">
          Replace <code>{"vlt_live_<…>"}</code> with the value you copied at issuance (shown
          once, never stored). Change the path to any route under your allowed prefixes.
          More integrations in <code>docs/CONSUMER_INTEGRATION.md</code>.
        </p>
      </section>

      <LiveTail proxyKeyId={row.id} />

      <div className="grid grid-cols-2 gap-6">
        <div className="card">
          <h2 className="font-semibold mb-3">Guardrails</h2>
          <Row
            label="Expires"
            value={row.expires_at ? new Date(row.expires_at).toLocaleString() : "never"}
          />
          <Row label="Daily cap" value={row.request_cap_day?.toString() ?? "none"} />
          <Row label="Monthly cap" value={row.request_cap_month?.toString() ?? "none"} />
          <Row
            label="Allowed paths"
            value={
              row.allowed_path_prefixes?.length
                ? row.allowed_path_prefixes.join(", ")
                : "any"
            }
          />
          <Row
            label="IP allowlist"
            value={row.ip_allowlist?.length ? row.ip_allowlist.join(", ") : "any"}
          />
        </div>
        <div className="card">
          <h2 className="font-semibold mb-3">Status</h2>
          <Row label="Created" value={new Date(row.created_at).toLocaleString()} />
          <Row
            label="Revoked"
            value={row.revoked_at ? new Date(row.revoked_at).toLocaleString() : "—"}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card py-5">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between text-sm py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200 text-right max-w-[60%] break-words">{value}</span>
    </div>
  );
}
